/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle } from '@/stores/layoutStore';
import { findNavItemByHref, navSections } from '@/modules/venta/components/VentaSidebar';
import { FavoritesContextMenu } from './FavoritesContextMenu';

// ── Helper: find which section owns a given href ──────────────────────────────
function findSectionLabel(href: string, tFn: (key: string) => string): string | null {
  for (const section of navSections) {
    if (section.items.some((item) => item.href === href)) {
      return tFn(section.titleKey);
    }
  }
  return null;
}

// ── FavoritesBar — center zone component ─────────────────────────────────────
// In palette mode it renders null (center zone is empty).
export default function FavoritesBar() {
  const favorites = useFavorites();
  const favoritesDisplayStyle = useFavoritesDisplayStyle();
  const reorderFavorites = useLayoutStore((s) => s.reorderFavorites);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [ctxMenu, setCtxMenu] = useState<{ href: string; x: number; y: number } | null>(null);
  const [removingHrefs, setRemovingHrefs] = useState<Set<string>>(new Set());
  const dragFrom = useRef<number | null>(null);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Animated removal: add to removingHrefs, wait 300ms, then call removeFavorite
  const handleRemove = useCallback((href: string) => {
    setRemovingHrefs((prev) => new Set(prev).add(href));
    removeTimerRef.current = setTimeout(() => {
      removeFavorite(href);
      setRemovingHrefs((prev) => { const n = new Set(prev); n.delete(href); return n; });
    }, 300);
  }, [removeFavorite]);

  useEffect(() => () => { if (removeTimerRef.current) clearTimeout(removeTimerRef.current); }, []);

  // Palette mode: center zone is empty — render null
  if (favoritesDisplayStyle === 'palette') return null;

  // Resolve nav items — skip hrefs that no longer exist in navSections
  const resolvedItems = favorites
    .map((href) => ({ href, navItem: findNavItemByHref(href) }))
    .filter((x): x is { href: string; navItem: NonNullable<ReturnType<typeof findNavItemByHref>> } => !!x.navItem);

  if (resolvedItems.length === 0) return null;

  const handleDragStart = (index: number) => { dragFrom.current = index; };
  const handleDrop = (toIndex: number) => {
    if (dragFrom.current !== null && dragFrom.current !== toIndex) {
      reorderFavorites(dragFrom.current, toIndex);
    }
    dragFrom.current = null;
  };

  const handleContextMenu = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setCtxMenu({ href, x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <div className="fav-bar">
        {favoritesDisplayStyle === 'icons' && resolvedItems.map(({ href, navItem }, i) => (
          <div
            key={href}
            className={`fav-icon-chip${removingHrefs.has(href) ? ' fav-item-removing' : ''}`}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(i)}
            onClick={() => navigate(href)}
            onContextMenu={(e) => handleContextMenu(e, href)}
            title={t(navItem.labelKey)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(href);
              }
            }}
          >
            <navItem.icon size={14} />
            <span className="fav-icon-tooltip">{t(navItem.labelKey)}</span>
          </div>
        ))}

        {favoritesDisplayStyle === 'chips' && resolvedItems.map(({ href, navItem }, i) => (
          <div
            key={href}
            className={`fav-chip${removingHrefs.has(href) ? ' fav-item-removing' : ''}`}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(i)}
            onClick={() => navigate(href)}
            onContextMenu={(e) => handleContextMenu(e, href)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(href);
              }
            }}
          >
            <navItem.icon size={12} />
            <span>{t(navItem.labelKey)}</span>
          </div>
        ))}
      </div>

      {ctxMenu && (
        <FavoritesContextMenu
          href={ctxMenu.href}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          onRemove={() => { handleRemove(ctxMenu.href); setCtxMenu(null); }}
        />
      )}

      <style>{favBarStyles}</style>
    </>
  );
}

// ── FavoritesPaletteButton — header-right star button + floating panel ────────
// Intended for placement in header-right when display style is 'palette'.
export function FavoritesPaletteButton() {
  const favorites = useFavorites();
  const favoritesDisplayStyle = useFavoritesDisplayStyle();
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelIn, setPanelIn] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ href: string; x: number; y: number } | null>(null);
  const [removingHrefs, setRemovingHrefs] = useState<Set<string>>(new Set());
  const btnRef = useRef<HTMLButtonElement>(null);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openPalette = useCallback(() => {
    setPanelVisible(true);
    setPaletteOpen(true);
    // Trigger enter animation next frame
    requestAnimationFrame(() => setPanelIn(true));
  }, []);

  const closePalette = useCallback(() => {
    setPanelIn(false);
    setPaletteOpen(false);
    closeTimerRef.current = setTimeout(() => setPanelVisible(false), 150);
  }, []);

  const togglePalette = useCallback(() => {
    if (paletteOpen) {
      closePalette();
    } else {
      openPalette();
    }
  }, [paletteOpen, closePalette, openPalette]);

  const handleRemove = useCallback((href: string) => {
    setRemovingHrefs((prev) => new Set(prev).add(href));
    removeTimerRef.current = setTimeout(() => {
      removeFavorite(href);
      setRemovingHrefs((prev) => { const n = new Set(prev); n.delete(href); return n; });
    }, 300);
  }, [removeFavorite]);

  useEffect(() => () => {
    if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, href: string) => {
    e.preventDefault();
    closePalette();
    setCtxMenu({ href, x: e.clientX, y: e.clientY });
  }, [closePalette]);

  // Only render in palette mode
  if (favoritesDisplayStyle !== 'palette') return null;

  // Resolve nav items
  const resolvedItems = favorites
    .map((href) => ({ href, navItem: findNavItemByHref(href) }))
    .filter((x): x is { href: string; navItem: NonNullable<ReturnType<typeof findNavItemByHref>> } => !!x.navItem);

  return (
    <>
      <div className="fav-palette-wrap">
        <button
          ref={btnRef}
          className={`fav-palette-trigger${paletteOpen ? ' fav-palette-trigger--open' : ''}`}
          onClick={togglePalette}
          type="button"
          aria-label={t('favorites.openPalette')}
          aria-expanded={paletteOpen}
        >
          <Star size={16} />
        </button>

        {panelVisible && (
          <>
            <div className="fav-palette-backdrop" onClick={closePalette} />
            <div
              className={`fav-palette-panel${panelIn ? ' fav-panel-in' : ' fav-panel-exit'}`}
              role="menu"
            >
              <div className="fav-palette-title">{t('favorites.title')}</div>
              <div className="fav-palette-grid">
                {resolvedItems.map(({ href, navItem }) => {
                  const sectionLabel = findSectionLabel(href, t);
                  return (
                    <button
                      key={href}
                      className={`fav-palette-item${removingHrefs.has(href) ? ' fav-item-removing' : ''}`}
                      onClick={() => { navigate(href); closePalette(); }}
                      onContextMenu={(e) => handleContextMenu(e, href)}
                      type="button"
                      role="menuitem"
                    >
                      <navItem.icon size={18} className="fav-palette-item-icon" />
                      <span className="fav-palette-item-text">
                        <span className="fav-palette-item-label">{t(navItem.labelKey)}</span>
                        {sectionLabel && (
                          <span className="fav-palette-item-section">{sectionLabel}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {ctxMenu && (
        <FavoritesContextMenu
          href={ctxMenu.href}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          onRemove={() => { handleRemove(ctxMenu.href); setCtxMenu(null); }}
        />
      )}

      <style>{favBarStyles}</style>
    </>
  );
}

const favBarStyles = `
  .fav-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    justify-content: center;
    padding: 0 12px;
    min-width: 0;
  }

  /* ── Icons mode ──────────────────────────────────────────────────────────── */
  .fav-icon-chip {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-bg-hover);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-brand-primary, #63b3ed);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.15s;
    flex-shrink: 0;
    animation: fav-item-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    user-select: none;
  }
  .fav-icon-chip:hover {
    background: var(--color-bg-tertiary);
    border-color: var(--color-brand);
    color: var(--color-brand-primary, #63b3ed);
    transform: translateY(-1px);
  }
  .fav-icon-tooltip {
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%) translateY(-4px) scale(0.9);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-primary);
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease-out, transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 1000;
  }
  .fav-icon-chip:hover .fav-icon-tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }

  /* ── Chips mode ──────────────────────────────────────────────────────────── */
  .fav-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 6px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-secondary);
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    flex-shrink: 0;
    animation: fav-item-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    user-select: none;
  }
  .fav-chip:hover {
    background: var(--color-bg-tertiary);
    border-color: var(--color-brand);
    color: var(--color-brand);
  }

  /* ── Item removal animation ──────────────────────────────────────────────── */
  .fav-item-removing {
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    transition: opacity 0.2s ease, max-width 0.25s ease 0.05s, padding 0.25s ease 0.05s, margin 0.25s ease 0.05s;
  }

  /* ── Palette trigger button (header-right icon button) ───────────────────── */
  .fav-palette-wrap {
    position: relative;
  }
  .fav-palette-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--color-brand-primary, #63b3ed);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .fav-palette-trigger:hover,
  .fav-palette-trigger--open {
    background: var(--color-bg-hover);
    color: var(--color-brand-primary, #63b3ed);
  }

  /* ── Palette panel ───────────────────────────────────────────────────────── */
  .fav-palette-backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
  }
  .fav-palette-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 320px;
    background: rgba(32, 37, 58, 0.94);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border: 1px solid rgba(99,179,237,0.18);
    border-radius: 14px;
    padding: 12px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.4);
    z-index: 1000;
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
    transition: opacity 0.15s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
  html[data-theme="light"] .fav-palette-panel {
    background: rgba(255,255,255,0.97);
    border-color: rgba(0,0,0,0.08);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
  }
  .fav-palette-panel.fav-panel-in {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  .fav-palette-panel.fav-panel-exit {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  .fav-palette-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--color-text-muted);
    margin-bottom: 10px;
    padding: 0 4px;
  }
  html[data-theme="light"] .fav-palette-title { color: #64748b; }
  .fav-palette-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .fav-palette-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    border: none;
    background: rgba(255,255,255,0.06);
    color: rgba(255,255,255,0.85);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s, color 0.12s;
    min-width: 0;
    overflow: hidden;
    width: 100%;
  }
  html[data-theme="light"] .fav-palette-item {
    background: rgba(0,0,0,0.04);
    color: #1e293b;
  }
  .fav-palette-item:hover {
    background: color-mix(in srgb, var(--color-brand) 15%, transparent);
    color: var(--color-brand);
  }
  .fav-palette-item-icon {
    flex-shrink: 0;
    opacity: 0.8;
    margin-top: 1px;
  }
  .fav-palette-item-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .fav-palette-item-label {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fav-palette-item-section {
    font-size: 9.5px;
    font-weight: 400;
    opacity: 0.55;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ── Entrance animation ──────────────────────────────────────────────────── */
  @keyframes fav-item-in {
    from { opacity: 0; transform: scale(0.7); }
    to   { opacity: 1; transform: scale(1); }
  }

  /* ── Hide bar on mobile ───────────────────────────────────────────────────── */
  @media (max-width: 768px) {
    .fav-bar { display: none; }
  }

  @media (max-width: 640px) {
    .fav-palette-panel {
      position: fixed;
      top: 52px;
      right: 8px;
      left: 8px;
      width: auto;
      max-height: 70vh;
      overflow-y: auto;
    }
    .fav-palette-backdrop {
      position: fixed;
      inset: 0;
      z-index: 999;
    }
    .fav-palette-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .fav-icon-chip, .fav-chip, .fav-palette-panel {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`;
